import { getConfig } from "@/config";
import { apiService } from "../api.service";
import {
	// Permissions API types
	type ActionToCheck,
	AuthApiError,
	type ExchangeRequest,
	PermissionsError,
	type SetInitialPasswordRequest,
	SetInitialPasswordRequestSchema,
	type UserInfo,
	UserInfoError,
	type UserPermissions,
	type UserPermissionsResponse,
	UserPermissionsResponseSchema,
	type ValidActionsRequest,
	type ValidActionsResponse,
} from "./auth.types";

export const AUTH_LOGOUT_REASON_KEY = "auth_logout_reason";

class AuthService {
	/**
	 * Log out via a top-level navigation so the API 302 to the IdP session-end
	 * URL is followed as a document request (not Axios/XHR, which Logto CORS-rejects).
	 */
	async logOut(): Promise<void> {
		if (typeof window === "undefined") {
			return;
		}

		const config = getConfig();
		const redirectUrl = `${window.location.origin}/signed-out`;
		const logoutUrl = `${config.api.baseUrl}/authentication/log-out?redirectUrl=${encodeURIComponent(redirectUrl)}`;
		window.location.assign(logoutUrl);
	}

	/**
	 * Exchange token for authentication
	 * POST /authentication/exchange
	 */
	async exchange(request: ExchangeRequest): Promise<void> {
		try {
			await apiService.post("/authentication/exchange", request);
		} catch (error: unknown) {
			throw new AuthApiError(
				"Failed to exchange token",
				"EXCHANGE_TOKEN_ERROR",
				{
					error: error instanceof Error ? error.message : "Unknown error",
					code: "EXCHANGE_TOKEN_ERROR",
					details: error instanceof Error ? error.stack : undefined,
				},
			);
		}
	}

	/**
	 * Set initial password for a user (e.g. after accepting invite from email)
	 * PUT /authentication/{userId}/initial-password
	 *
	 * @param userId - The user's UUID
	 * @param request - Object containing password and token (setPasswordToken from invite link)
	 */
	async setInitialPassword(
		userId: string,
		request: SetInitialPasswordRequest,
	): Promise<void> {
		try {
			const validated = SetInitialPasswordRequestSchema.parse(request);
			await apiService.put(
				`/authentication/${userId}/initial-password`,
				validated,
			);
		} catch (error: unknown) {
			throw new AuthApiError(
				"Failed to set initial password",
				"SET_INITIAL_PASSWORD_ERROR",
				{
					error: error instanceof Error ? error.message : "Unknown error",
					userId,
					details: error instanceof Error ? error.stack : undefined,
				},
			);
		}
	}

	/**
	 * Get current user information
	 * Handles all error responses with detailed error context
	 */
	async getUserInfo(): Promise<UserInfo> {
		try {
			const data = await apiService.get<UserInfo>("/authentication/user-info");
			console.log("userInfo", data);
			return data;
		} catch (error: unknown) {
			// Handle different error scenarios
			// if (error && typeof error === "object" && "response" in error) {
			// 	const response = (
			// 		error as {
			// 			response?: {
			// 				status?: number;
			// 				headers?: Record<string, string>;
			// 				data?: { message?: string };
			// 			};
			// 		}
			// 	).response;

			// 	switch (response?.status) {
			// 		case 401:
			// 			throw new UserInfoError(
			// 				"Authentication required to access user information",
			// 				"USER_INFO_UNAUTHORIZED",
			// 				{
			// 					statusCode: 401,
			// 					endpoint: "/authentication/user-info",
			// 					suggestion: "Please log in again",
			// 				},
			// 				401,
			// 			);

			// 		case 403:
			// 			throw new UserInfoError(
			// 				"Access forbidden - insufficient permissions",
			// 				"USER_INFO_FORBIDDEN",
			// 				{
			// 					statusCode: 403,
			// 					endpoint: "/authentication/user-info",
			// 					suggestion: "Contact administrator for access",
			// 				},
			// 				403,
			// 			);

			// 		case 404:
			// 			throw new UserInfoError(
			// 				"User information not found",
			// 				"USER_INFO_NOT_FOUND",
			// 				{
			// 					statusCode: 404,
			// 					endpoint: "/authentication/user-info",
			// 					suggestion: "User may not exist or has been deleted",
			// 				},
			// 				404,
			// 			);

			// 		case 429:
			// 			throw new UserInfoError(
			// 				"Too many requests - rate limit exceeded",
			// 				"USER_INFO_RATE_LIMITED",
			// 				{
			// 					statusCode: 429,
			// 					endpoint: "/authentication/user-info",
			// 					suggestion: "Please wait before making another request",
			// 					retryAfter: response.headers?.["retry-after"],
			// 				},
			// 				429,
			// 			);

			// 		case 500:
			// 		case 502:
			// 		case 503:
			// 		case 504:
			// 			throw new UserInfoError(
			// 				"Server error occurred while fetching user information",
			// 				"USER_INFO_SERVER_ERROR",
			// 				{
			// 					statusCode: response.status,
			// 					endpoint: "/authentication/user-info",
			// 					suggestion: "Please try again later or contact support",
			// 					serverMessage: response.data?.message,
			// 				},
			// 				response.status,
			// 			);

			// 		default:
			// 			throw new UserInfoError(
			// 				`Unexpected error occurred (${response?.status ?? "unknown"})`,
			// 				"USER_INFO_UNKNOWN_ERROR",
			// 				{
			// 					statusCode: response?.status,
			// 					endpoint: "/authentication/user-info",
			// 					responseData: response?.data,
			// 				},
			// 				response?.status,
			// 			);
			// 	}
			// }

			// // Handle network errors, timeouts, etc.
			// if (error instanceof Error) {
			// 	if (error.message.includes("timeout")) {
			// 		throw new UserInfoError(
			// 			"Request timeout while fetching user information",
			// 			"USER_INFO_TIMEOUT",
			// 			{
			// 				endpoint: "/authentication/user-info",
			// 				suggestion: "Check your internet connection and try again",
			// 				originalError: error.message,
			// 			},
			// 		);
			// 	}

			// 	if (error.message.includes("Network Error")) {
			// 		throw new UserInfoError(
			// 			"Network error while fetching user information",
			// 			"USER_INFO_NETWORK_ERROR",
			// 			{
			// 				endpoint: "/authentication/user-info",
			// 				suggestion: "Check your internet connection",
			// 				originalError: error.message,
			// 			},
			// 		);
			// 	}
			// }

			// Fallback for any other errors
			throw new UserInfoError(
				"Failed to fetch user information",
				"USER_INFO_ERROR",
				{
					endpoint: "/authentication/user-info",
					error: error instanceof Error ? error.message : "Unknown error",
					stack: error instanceof Error ? error.stack : undefined,
				},
			);
		}
	}

	// =============================================================================
	// PERMISSIONS API METHODS - Based on Swagger Specification
	// =============================================================================

	/**
	 * Get current user's permissions
	 * GET /authentication/user-info/permissions
	 */
	async getCurrentUserPermissions(): Promise<UserPermissions> {
		try {
			const data = await apiService.get<UserPermissionsResponse>(
				"/authentication/user-info/permissions",
			);
			const response = UserPermissionsResponseSchema.parse(data);

			// Transform API response to internal format
			return this.transformPermissionsResponse(response);
		} catch (error: unknown) {
			throw new PermissionsError(
				"Failed to fetch current user permissions",
				"CURRENT_USER_PERMISSIONS_ERROR",
				{
					endpoint: "/authentication/user-info/permissions",
					error: error instanceof Error ? error.message : "Unknown error",
					stack: error instanceof Error ? error.stack : undefined,
				},
			);
		}
	}

	/**
	 * Get specific user's permissions
	 * GET /users/{userId}/permissions
	 */
	async getUserPermissions(userId: string): Promise<UserPermissions> {
		if (!userId) {
			throw new PermissionsError(
				"User ID is required",
				"USER_PERMISSIONS_INVALID_PARAMS",
				{
					endpoint: `/users/${userId}/permissions`,
					userId,
				},
				400,
			);
		}

		try {
			const data = await apiService.get<UserPermissionsResponse>(
				`/users/${userId}/permissions`,
			);
			const response = UserPermissionsResponseSchema.parse(data);

			// Transform API response to internal format
			return this.transformPermissionsResponse(response);
		} catch (error: unknown) {
			throw new PermissionsError(
				`Failed to fetch permissions for user ${userId}`,
				"USER_PERMISSIONS_ERROR",
				{
					endpoint: `/users/${userId}/permissions`,
					userId,
					error: error instanceof Error ? error.message : "Unknown error",
					stack: error instanceof Error ? error.stack : undefined,
				},
			);
		}
	}

	/**
	 * Validate multiple actions at once
	 * POST /valid-actions
	 */
	async validateActions(
		actions: ValidActionsRequest,
	): Promise<ValidActionsResponse> {
		if (!Array.isArray(actions) || actions.length === 0) {
			throw new PermissionsError(
				"Actions array is required and cannot be empty",
				"VALIDATE_ACTIONS_INVALID_PARAMS",
				{
					endpoint: "/valid-actions",
					actionsCount: actions?.length || 0,
				},
				400,
			);
		}

		// Validate each action has required name field
		const invalidActions = actions.filter((action) => !action.name);
		if (invalidActions.length > 0) {
			throw new PermissionsError(
				"All actions must have a 'name' field",
				"VALIDATE_ACTIONS_MISSING_NAME",
				{
					endpoint: "/valid-actions",
					invalidActionsCount: invalidActions.length,
					totalActions: actions.length,
				},
				400,
			);
		}

		try {
			const data = await apiService.post<ValidActionsResponse>(
				"/valid-actions",
				actions,
			);
			// const results = ValidActionsResponseSchema.parse(data);
			return data;
		} catch (error: unknown) {
			throw new PermissionsError(
				"Failed to validate actions",
				"VALIDATE_ACTIONS_ERROR",
				{
					endpoint: "/valid-actions",
					actionsCount: actions.length,
					error: error instanceof Error ? error.message : "Unknown error",
					stack: error instanceof Error ? error.stack : undefined,
				},
			);
		}
	}

	/**
	 * Check single action permission (convenience method)
	 * Uses validateActions internally for consistency
	 */
	async checkAction(action: ActionToCheck): Promise<boolean> {
		try {
			const results = await this.validateActions([action]);
			return results[0]?.validForCurrentUser || false;
		} catch (error: unknown) {
			// Log the error but return false for permission checks
			console.error("Permission check failed:", error);
			return false;
		}
	}

	/**
	 * Batch permission check for multiple actions (convenience method)
	 * Returns a Map for easy lookup by action name
	 */
	async checkActions(actions: ActionToCheck[]): Promise<Map<string, boolean>> {
		try {
			const results = await this.validateActions(actions);
			const permissionMap = new Map<string, boolean>();

			results.forEach((result, index) => {
				const actionName = actions[index]?.name;
				if (actionName) {
					permissionMap.set(actionName, result.validForCurrentUser);
				}
			});

			return permissionMap;
		} catch (error: unknown) {
			// Log the error but return empty map for permission checks
			console.error("Batch permission check failed:", error);
			return new Map();
		}
	}

	/**
	 * Transform API permissions response to internal format
	 * @private
	 */
	private transformPermissionsResponse(
		response: UserPermissionsResponse,
	): UserPermissions {
		const permissions: UserPermissions["permissions"] = [];
		const organizations: UserPermissions["organizations"] = [];
		const platformRoles: UserPermissions["platformRoles"] = [];

		// Transform platform roles
		for (const platformRole of response.platformRoles || []) {
			platformRoles.push({
				id: platformRole.code.toString(),
				name: platformRole.name,
				description: platformRole.description,
				code: platformRole.code,
			});
		}

		// Transform organizations and their permissions/roles
		for (const orgData of response.organizationsAndPermissions || []) {
			const {
				organization,
				permissions: orgPermissions,
				organizationRoles,
			} = orgData;

			// Add organization to the list (only id and name are provided by API)
			organizations.push({
				id: organization.id,
				name: organization.name,
			});

			// Add each permission with organization context
			for (const permission of orgPermissions || []) {
				permissions.push({
					id: permission.id,
					name: permission.name,
					description: permission.description,
					organizationId: organization.id,
					organizationName: organization.name,
				});
			}

			// Add organization roles as permissions (for compatibility)
			// Note: API only returns name field for organization roles
			for (const orgRole of organizationRoles || []) {
				permissions.push({
					id: `${organization.id}-${orgRole.name}`, // Generate ID from org + role name
					name: orgRole.name,
					description: `Organization Role: ${orgRole.name}`,
					organizationId: organization.id,
					organizationName: organization.name,
					isRole: true, // Flag to distinguish from regular permissions
				});
			}
		}

		return {
			permissions,
			organizations,
			platformRoles,
		};
	}
}

// Export singleton instance
export const authService = new AuthService();
